/**
 * Memories guide — managing end-user memories for personalized teammate context.
 *
 * Covers three types of teammate memory: user metadata (API-created),
 * saved memories (teammate-created during runs), and previous run history.
 */

export const memoriesContent = `
Memories are persistent facts about end-users that are automatically injected into the teammate's context during runs. Use them to personalize teammate behavior.

## How Memories Work

1. You create memories via the API (or the teammate saves them during runs)
2. When a run executes for that \`user_id\`, memories are included in the system prompt
3. The teammate uses these facts to personalize its responses and actions

## User Metadata

Pre-populate memories via the API to give teammates immediate context about your users. Think of these as user profile data that personalizes every interaction.

### Create a Memory

\`\`\`python
from m8tes import M8tes

client = M8tes()

client.memories.create(user_id="cust_123", content="Prefers email over Slack")
client.memories.create(user_id="cust_123", content="Timezone: America/New_York")
client.memories.create(user_id="cust_123", content="VIP customer — escalate issues to manager")
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/memories \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"user_id": "cust_123", "content": "Prefers email over Slack"}'
\`\`\`

Each memory is a short text (max 300 characters). Duplicates are automatically detected (case-insensitive, whitespace-normalized) and return a \`409 Conflict\` response.

### List Memories

\`\`\`python
page = client.memories.list(user_id="cust_123")
for memory in page.data:
    print(f"[{memory.id}] {memory.content} (source: {memory.source})")
\`\`\`

\`\`\`bash
curl "https://m8tes.ai/api/v2/memories?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

The \`source\` field is \`"api"\` for memories you create, or \`"agent"\` for memories the teammate saves during runs.

### Delete a Memory

\`\`\`python
client.memories.delete(7, user_id="cust_123")
\`\`\`

\`\`\`bash
curl -X DELETE "https://m8tes.ai/api/v2/memories/7?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

The \`user_id\` parameter is required on delete to prevent accidental deletion of another user's memories.

### Pre-Populate During Onboarding

Seed memories before the first run to give the teammate immediate context:

\`\`\`python
def onboard_user(user_id: str, preferences: dict):
    client = M8tes()
    if preferences.get("timezone"):
        client.memories.create(user_id=user_id, content=f"Timezone: {preferences['timezone']}")
    if preferences.get("language"):
        client.memories.create(user_id=user_id, content=f"Preferred language: {preferences['language']}")
    if preferences.get("communication"):
        client.memories.create(user_id=user_id, content=f"Prefers {preferences['communication']} for notifications")

onboard_user("cust_456", {"timezone": "Europe/London", "language": "English", "communication": "email"})
\`\`\`

## Saved Memories (Built-in Tool)

During runs, the teammate can save memories via its built-in memory tool. These are stored with \`source: "agent"\` and follow the same isolation rules.

The teammate decides what to remember based on the conversation — things like user preferences, key decisions, or recurring patterns.

To **disable** saved memories for a specific run:

\`\`\`python
run = client.runs.create(
    teammate_id=bot.id,
    message="One-off analysis — don't save anything",
    memory=False,
    stream=False,
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "One-off analysis", "memory": false, "stream": false}'
\`\`\`

## Previous Runs (Built-in Tool)

Runs automatically include context from previous runs for the same user. This lets teammates build on past work without you managing conversation history manually.

The teammate can search through previous run outputs to recall what was done before — giving it continuity across sessions.

To **disable** previous run history for a specific run:

\`\`\`python
run = client.runs.create(
    teammate_id=bot.id,
    message="Fresh analysis — ignore previous context",
    history=False,
    stream=False,
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Fresh analysis", "history": false, "stream": false}'
\`\`\`

## Limits

- **Max length**: 300 characters per memory
- **Deduplication**: identical content (case-insensitive, whitespace-normalized) for the same user returns \`409 Conflict\`
- **Scoping**: memories are strictly isolated per \`user_id\`

## What's Next

- [Users](/docs/users) — multi-tenancy and data isolation with \`user_id\`
- [Permissions](/docs/permissions) — control tool access with approval modes
`;
